import React, { useEffect, useMemo, useState } from 'react'
import { Layout } from '../../components/Layout/Layout'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

/**
 * Read-only audit view of UsageLog records — periodic per-account aggregated
 * usage summaries submitted by carriers (UIC 30301 §5.4).
 */
export function UsageLogs() {
  const { t } = useContent()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    setLoading(true)
    api
      .get('/api/usagelogs')
      .then((r) => setRows(Array.isArray(r.data) ? r.data : r.data.content || []))
      .catch((err) => setError(err.response?.data?.message || t('usageLogs.loadFailed')))
      .finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return rows
    const s = search.toLowerCase()
    return rows.filter(
      (l) =>
        (l.transactionId || '').toLowerCase().includes(s) ||
        (l.agreementId || '').toLowerCase().includes(s) ||
        (l.accountId || '').toLowerCase().includes(s) ||
        (l.accountProviderRics || '').toLowerCase().includes(s)
    )
  }, [rows, search])

  return (
    <Layout crumb={t('usageLogs.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h">{t('usageLogs.title')}</h1>
              <div className="page-head__sub">
                <T k="usageLogs.sub" />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    filtered,
                    [
                      { key: 'transactionId', label: t('usageLogs.csv.transaction') },
                      { key: 'agreementId', label: t('usageLogs.csv.agreement') },
                      { key: 'accountId', label: t('usageLogs.csv.account') },
                      { key: 'accountProviderName', label: t('usageLogs.csv.accountProvider') },
                      { key: 'accountProviderRics', label: t('usageLogs.csv.apRics') },
                      { label: t('usageLogs.csv.periodStart'), get: (l) => l.startLogPeriod || '' },
                      { label: t('usageLogs.csv.periodEnd'), get: (l) => l.endLogPeriod || '' },
                      { label: t('usageLogs.csv.created'), get: (l) => l.createdAt || '' },
                    ],
                    'mas-usage-logs'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('usageLogs.exportCsv')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{ marginBottom: 14 }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('usageLogs.search.placeholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              {t('usageLogs.count', { shown: filtered.length, total: rows.length })}
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('usageLogs.column.transaction')}</th>
                  <th>{t('usageLogs.column.agreement')}</th>
                  <th>{t('usageLogs.column.account')}</th>
                  <th>{t('usageLogs.column.accountProvider')}</th>
                  <th>{t('usageLogs.column.periodStart')}</th>
                  <th>{t('usageLogs.column.periodEnd')}</th>
                  <th>{t('usageLogs.column.created')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="usageLogs.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="usageLogs.empty" />
                    </td>
                  </tr>
                ) : (
                  filtered.map((l, i) => (
                    <tr key={l.id || i}>
                      <td className="id mono" data-label={t('usageLogs.column.transaction')} style={{ fontSize: 11 }}>{l.transactionId || '—'}</td>
                      <td className="id" data-label={t('usageLogs.column.agreement')}>{l.agreementId || '—'}</td>
                      <td className="id" data-label={t('usageLogs.column.account')}>{l.accountId || '—'}</td>
                      <td className="muted" data-label={t('usageLogs.column.accountProvider')}>
                        {l.accountProviderName || '—'}
                        {l.accountProviderRics ? (
                          <span className="muted mono" style={{ fontSize: 11, marginLeft: 6 }}>
                            ({l.accountProviderRics})
                          </span>
                        ) : null}
                      </td>
                      <td className="muted mono" data-label={t('usageLogs.column.periodStart')} style={{ fontSize: 11 }}>
                        {l.startLogPeriod ? new Date(l.startLogPeriod).toLocaleString('en-GB') : '—'}
                      </td>
                      <td className="muted mono" data-label={t('usageLogs.column.periodEnd')} style={{ fontSize: 11 }}>
                        {l.endLogPeriod ? new Date(l.endLogPeriod).toLocaleString('en-GB') : '—'}
                      </td>
                      <td className="muted mono" data-label={t('usageLogs.column.created')} style={{ fontSize: 11 }}>
                        {l.createdAt ? new Date(l.createdAt).toLocaleString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      </div>
    </Layout>
  )
}
